import { usePortfolio } from '../../context/PortfolioContext';
import { useRef, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const CURRENCY_COLORS = {
  'USD': '#00d4ff',    // cyan
  'EUR': '#22c55e',    // green
  'GBP': '#f97316',    // orange
  'CAD': '#ec4899',    // pink
  'JPY': '#8b5cf6',    // purple
  'CHF': '#f59e0b',    // amber
  'AUD': '#06b6d4',    // teal
  'SEK': '#84cc16',    // lime
  'NOK': '#ef4444',    // red
  'DKK': '#6366f1',    // indigo
};

const DEFAULT_COLORS = [
  '#a855f7', '#14b8a6', '#f97316', '#eab308', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f59e0b', '#6366f1'
];

function CurrencyDistributionChart() {
  const { portfolio, isLoading } = usePortfolio();
  
  // Keep last valid data to prevent flashing during refreshes
  const lastValidDataRef = useRef(null);
  const hasDataEverLoadedRef = useRef(false);
  
  // Calculate current currency data
  const currentCurrencyData = useMemo(() => {
    if (!portfolio || !portfolio.holdings || portfolio.holdings.length === 0) {
      return null;
    }
    
    const currencyValues = {};
    let totalValue = 0;

    portfolio.holdings.forEach(holding => {
      const currency = holding.currency || holding.price_currency || 'USD';
      const marketValue = (holding.quantity || 0) * (holding.price_gbp || holding.price || 0);
      
      if (!currencyValues[currency]) {
        currencyValues[currency] = 0;
      }
      currencyValues[currency] += marketValue;
      totalValue += marketValue;
    });

    const sortedCurrencies = Object.entries(currencyValues)
      .sort((a, b) => b[1] - a[1])
      .filter(([_, value]) => value > 0);
    
    if (sortedCurrencies.length === 0) {
      return null;
    }
    
    return {
      sortedCurrencies,
      totalValue
    };
  }, [portfolio]);
  
  // Update last valid data when we have valid data
  if (currentCurrencyData) {
    lastValidDataRef.current = currentCurrencyData;
    hasDataEverLoadedRef.current = true;
  }
  
  // Only use cached data if currently loading (prevents flashing during refresh)
  // If data is loaded but empty (sold all positions), show empty state
  const isCurrentlyLoading = isLoading && !currentCurrencyData;
  const displayData = isCurrentlyLoading ? (lastValidDataRef.current || currentCurrencyData) : currentCurrencyData;
  const isInitialLoading = isLoading && !hasDataEverLoadedRef.current;
  const hasNoDataEver = !displayData && !hasDataEverLoadedRef.current;

  if (isInitialLoading || hasNoDataEver) {
    return (
      <div className="chart-container">
        <div className="chart-header">
          <span className="chart-title">Currency Distribution</span>
        </div>
        <div style={{ height: '250px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span className="text-muted">No currency data available</span>
        </div>
      </div>
    );
  }

  // Use display data
  const { sortedCurrencies, totalValue } = displayData;

  const chartData = {
    labels: sortedCurrencies.map(([currency, _]) => currency),
    datasets: [
      {
        data: sortedCurrencies.map(([_, value]) => value),
        backgroundColor: sortedCurrencies.map(([currency], index) =>
          CURRENCY_COLORS[currency] || DEFAULT_COLORS[index % DEFAULT_COLORS.length]
        ),
        borderColor: 'rgba(12, 28, 48, 0.8)',
        borderWidth: 1,
        borderRadius: 4,
        barThickness: 20,
      },
    ],
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value);
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y', // Horizontal bar chart - currencies on Y axis
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(12, 28, 48, 0.96)',
        borderColor: 'rgba(0, 212, 255, 0.35)',
        borderWidth: 1,
        titleColor: '#dff0ff',
        bodyColor: '#dff0ff',
        padding: 10,
        callbacks: {
          label: (context) => {
            const value = context.raw;
            const percentage = ((value / totalValue) * 100).toFixed(1);
            return `${context.label}: ${formatCurrency(value)} (${percentage}%)`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          color: 'rgba(127, 140, 160, 0.1)',
          drawBorder: false,
        },
        ticks: {
          color: '#7f8ca0',
          font: { size: 10 },
          callback: (value) => {
            return new Intl.NumberFormat('en-GB', {
              notation: 'compact',
              maximumFractionDigits: 1,
            }).format(value);
          },
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#dff0ff',
          font: { size: 12, weight: '500' },
        },
      },
    },
  };

  return (
    <div className="chart-container">
      <div className="chart-header">
        <span className="chart-title">Currency Distribution</span>
        <span className="data-table-badge">By Market Value</span>
      </div>

      <div style={{ height: '200px' }}>
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
}

export default CurrencyDistributionChart;
